/* eslint-disable react-refresh/only-export-components -- provider + hooks
   colocated by convention (matches AuthContext / SelectedHospitalContext). */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react'

/**
 * The top bar's filter box, and the table it filters (M23-T03).
 *
 * The box lives in the shell's header — every frame draws it there — but the
 * rows it filters live on the page, so the two are joined here rather than by
 * lifting the box into each page.
 *
 * **On the honesty of the label.** All twelve frames call it "Search this page"
 * (M23 gap A5). No admin search endpoint exists on any of the twelve, so what
 * ships filters the rows already loaded, client-side, and says so. Where the
 * table is paginated it says *that* too — a box labelled "Search this page"
 * that quietly ignores everything past page one is worse than no box, because
 * an empty result reads as "there is none" rather than "not on this page".
 *
 * `active` is false whenever no table is mounted, and the shell disables the
 * input — a control that cannot do anything should not look as though it can.
 */
type TableFilterValue = {
  query: string
  setQuery: (q: string) => void
  active: boolean
  paginated: boolean
  /** What the box has settled on, `SEARCH_DEBOUNCE_MS` after the last
   *  keystroke. Only what reaches the SERVER is debounced; `query` above stays
   *  immediate, because a laggy text box is its own bug. */
  debounced: string
  /** The queue name when the mounted page searches server-side, else null. */
  serverQueue: string | null
  /** Rows the page is currently showing for the term, for the count line. */
  resultCount: number | null
  setResultCount: (n: number | null) => void
  register: (paginated: boolean) => () => void
  registerServer: (queue: string) => () => void
}

const TableFilterContext = createContext<TableFilterValue>({
  query: '',
  setQuery: () => {},
  active: false,
  paginated: false,
  debounced: '',
  serverQueue: null,
  resultCount: null,
  setResultCount: () => {},
  register: () => () => {},
  registerServer: () => () => {},
})

/** How long the box waits before asking the server. Long enough that typing a
 *  surname is one request, short enough that it does not feel stuck. */
export const SEARCH_DEBOUNCE_MS = 300

export function TableFilterProvider({ children }: { children: ReactNode }) {
  const [query, setQuery] = useState('')
  const [debounced, setDebounced] = useState('')
  const [tables, setTables] = useState(0)
  const [paginated, setPaginated] = useState(false)
  const [serverQueue, setServerQueue] = useState<string | null>(null)
  const [resultCount, setResultCount] = useState<number | null>(null)

  // Debounce only what reaches the server. The input itself stays immediate —
  // a laggy text box is its own bug.
  useEffect(() => {
    const id = setTimeout(() => setDebounced(query), SEARCH_DEBOUNCE_MS)
    return () => clearTimeout(id)
  }, [query])

  const register = useCallback((isPaginated: boolean) => {
    setTables((n) => n + 1)
    setPaginated(isPaginated)
    return () => {
      setTables((n) => n - 1)
      setPaginated(false)
    }
  }, [])

  const registerServer = useCallback((queue: string) => {
    setServerQueue(queue)
    setTables((n) => n + 1)
    return () => {
      setServerQueue(null)
      setTables((n) => n - 1)
    }
  }, [])

  const value = useMemo(
    () => ({
      query,
      setQuery,
      active: tables > 0,
      paginated,
      serverQueue,
      resultCount,
      setResultCount,
      register,
      registerServer,
      debounced,
    }),
    [query, tables, paginated, serverQueue, resultCount, register, registerServer, debounced],
  )
  return (
    <TableFilterContext.Provider value={value}>
      {children}
    </TableFilterContext.Provider>
  )
}

/** For the shell's header input. */
export function useTableFilter(): TableFilterValue {
  return useContext(TableFilterContext)
}

/**
 * For a table: registers itself as filterable for as long as it is mounted,
 * and returns the current query.
 */
export function useFilterQuery(paginated: boolean): string {
  const { query, register } = useContext(TableFilterContext)
  useEffect(() => register(paginated), [register, paginated])
  return query
}

/**
 * For a page that searches SERVER-side: registers the queue for as long as it
 * is mounted and returns the debounced term to feed a query key.
 *
 * The page that calls this is the page that sends `q`, so the box can never
 * claim a server search the endpoint does not do — see `searchable.ts`.
 */
export function useServerSearch(queue: string): {
  term: string
  setResultCount: (n: number | null) => void
} {
  const ctx = useContext(TableFilterContext)
  const { registerServer, setResultCount } = ctx
  useEffect(() => registerServer(queue), [registerServer, queue])
  return { term: ctx.debounced.trim(), setResultCount }
}

/** Clears the box when the route changes, so a filter cannot follow you. */
export function useClearFilterOn(key: string): void {
  const { setQuery } = useContext(TableFilterContext)
  useEffect(() => setQuery(''), [key, setQuery])
}
